import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Product, CartItem, Cart } from '@/types'

interface CartState extends Cart {
  // Cart state is included from Cart interface
}

interface CartActions {
  addItem: (product: Product, quantity?: number) => void
  removeItem: (productId: number) => void
  updateQuantity: (productId: number, quantity: number) => void
  clearCart: () => void
  applyDiscount: (amount: number) => void
  calculateTotals: () => void
}

type CartStore = CartState & CartActions

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      // Initial state
      items: [],
      total_items: 0,
      total_amount: 0,
      discount_amount: 0,
      final_amount: 0,

      // Actions
      addItem: (product: Product, quantity = 1) => {
        const state = get()
        const existingItemIndex = state.items.findIndex(
          item => item.product.product_id === product.product_id
        )

        let newItems: CartItem[]

        if (existingItemIndex >= 0) {
          // Update existing item
          newItems = state.items.map((item, index) =>
            index === existingItemIndex
              ? {
                  ...item,
                  quantity: item.quantity + quantity,
                  total_price: (item.quantity + quantity) * item.unit_price,
                }
              : item
          )
        } else {
          // Add new item
          const newItem: CartItem = {
            product,
            quantity,
            unit_price: product.unit_price,
            total_price: quantity * product.unit_price,
          }
          newItems = [...state.items, newItem]
        }

        set({ items: newItems })
        get().calculateTotals()
      },

      removeItem: (productId: number) => {
        const state = get()
        const newItems = state.items.filter(
          item => item.product.product_id !== productId
        )
        set({ items: newItems })
        get().calculateTotals()
      },

      updateQuantity: (productId: number, quantity: number) => {
        if (quantity <= 0) {
          get().removeItem(productId)
          return
        }

        const state = get()
        const newItems = state.items.map(item =>
          item.product.product_id === productId
            ? {
                ...item,
                quantity,
                total_price: quantity * item.unit_price,
              }
            : item
        )
        set({ items: newItems })
        get().calculateTotals()
      },

      clearCart: () => {
        set({
          items: [],
          total_items: 0,
          total_amount: 0,
          discount_amount: 0,
          final_amount: 0,
        })
      },

      applyDiscount: (amount: number) => {
        set({ discount_amount: amount })
        get().calculateTotals()
      },

      calculateTotals: () => {
        const state = get()
        const total_items = state.items.reduce((sum, item) => sum + item.quantity, 0)
        const total_amount = state.items.reduce((sum, item) => sum + item.total_price, 0)
        const final_amount = Math.max(0, total_amount - state.discount_amount)

        set({
          total_items,
          total_amount,
          final_amount,
        })
      },
    }),
    {
      name: 'cart-storage',
      partialize: (state) => ({
        items: state.items,
        discount_amount: state.discount_amount,
      }),
    }
  )
)

// Helper functions
export const getCartItemCount = () => {
  const state = useCartStore.getState()
  return state.total_items
}

export const getCartTotal = () => {
  const state = useCartStore.getState()
  return state.final_amount
}

export const isProductInCart = (productId: number) => {
  const state = useCartStore.getState()
  return state.items.some(item => item.product.product_id === productId)
}

export const getProductQuantityInCart = (productId: number) => {
  const state = useCartStore.getState()
  const item = state.items.find(item => item.product.product_id === productId)
  return item?.quantity || 0
}
